import * as React from 'react';
import styled from 'styled-components';
import type { MenuItem as MenuItemType } from '../../containers/DashBoard/helper';
import { SGriditemA, SGriditemLink } from '../GridItemLink/SGridItemLink';
import { NotificationBubble } from '../NotificationBubble/NotificationBubble';
import isElectron from 'is-electron';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { clearCreateData } from '../../containers/LoneWorkerTimer/loneWorkerTimerSlice/actionCreator';
import { clearCreateData as clearCreateDataPanic } from '../../containers/PanicButton/PanicButtonSlice/actionCreators';
import { setActiveTab } from '../../containers/Checklists/checklistsSlice';
import { ChecklistStatus, ActiveDocumentsTab } from '../../utils/enums';
import { setActiveTab as setActiveDocumentsTab } from '../../containers/Documents/documentsSlice';

const SMenuName = styled.p`
  font-size: 0.875rem;
  font-family: 'Roboto-Regular';
  color: ${({ theme }) => theme.palette.text.menuPrimary};
  text-align: center;
  text-align-last: center;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SIconWrapper = styled.div`
  width: 36%;
  height: 36%;
  margin-bottom: 12%;
  min-width: 0;
  position: relative;
  svg {
    height: 100%;
    width: 100%;
  }
`;

const SOverridecSGridItemLink = styled(SGriditemLink)`
  width: 100%;
`;

const SOverridenSGridItem = styled(SGriditemA)`
  width: 100%;
  cursor: pointer;
`;

type IMenuItemProps = {
  role?: string;
  titleText: string;
  hidden: boolean;
  disabled: boolean;
  $needDangerStyle: boolean;
  notificationNum: number | '!' | null;
  isNotificationDanger?: boolean;
  onSelect?: (navLink: string) => void;
} & Omit<MenuItemType, 'tx'>;

export const MenuItem = (props: IMenuItemProps) => {
  const {
    hidden,
    navLink,
    externalUrl,
    titleText,
    Icon,
    $needDangerStyle,
    notificationNum,
    isNotificationDanger,
    disabled = false,
    onSelect,
  } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleExternalLinkClick = () => {
    if (!externalUrl) return;
    if (isElectron()) {
      window.electronApi?.sendOpenLink(externalUrl);
    } else {
      window.open(externalUrl, '_blank');
    }
  };

  const renderContent = () => (
    <>
      <SIconWrapper role="iconWrapper">
        {notificationNum && (
          <NotificationBubble notification={notificationNum} isDanger={isNotificationDanger} />
        )}
        <Icon role="icon" />
      </SIconWrapper>
      <SMenuName role="menuName">{titleText}</SMenuName>
    </>
  );

  const handleItemClick = () => {
    if (externalUrl) {
      handleExternalLinkClick();
    } else if (onSelect) {
      onSelect(navLink);
    } else {
      //Here you can call actions before navigation
      if (navLink === 'panic/create') {
        dispatch(clearCreateDataPanic());
      }
      if (navLink === 'aloneWorker/create') {
        dispatch(clearCreateData());
      }
      if (navLink === 'checklists') {
        dispatch(setActiveTab(ChecklistStatus.Started));
      }
      if (navLink === 'documents') {
        dispatch(setActiveDocumentsTab(ActiveDocumentsTab.Documents));
      }
      navigate(`/${navLink}`);
    }
  };

  if (hidden) return <></>;

  return (
    <>
      {externalUrl ? (
        <SOverridenSGridItem
          role={`menuItem-${titleText}`}
          disabled={disabled}
          $needDangerStyle={$needDangerStyle}
          onClick={handleItemClick}
          rel={'noopener noreferrer'}
        >
          {renderContent()}
        </SOverridenSGridItem>
      ) : onSelect ? (
        <SOverridecSGridItemLink
          role={`menuItem-${titleText}`}
          disabled={disabled}
          onClick={handleItemClick}
          as="div"
          $needDangerStyle={$needDangerStyle}
        >
          {renderContent()}
        </SOverridecSGridItemLink>
      ) : (
        <SOverridecSGridItemLink
          role={`menuItem-${titleText}`}
          disabled={disabled}
          $needDangerStyle={$needDangerStyle}
          as="div"
          onClick={handleItemClick}
        >
          {renderContent()}
        </SOverridecSGridItemLink>
      )}
    </>
  );
};
